import React, { useRef } from 'react';
import { TextInput, ActionIcon, Group, Tooltip, Box, Text, Loader, Flex } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { FieldLabelWithRequired } from '../entities/renderDynamicField';
import { ReferenceFieldViewIcon } from './ReferenceFieldViewIcon';
import { MultiReferenceEntityBadge } from './MultiReferenceEntityBadge';
import { useReferenceIdInput } from '../../hooks/useReferenceIdInput';
import type { ResolvedReferenceEntity } from '../../utils/referenceIdLookup';

export interface ReferenceIdEntryConfig {
    entityTypes: string[];
    entityId: string | null;
    displayName: string;
    onCommit: (entity: ResolvedReferenceEntity) => void;
    onClear: () => void;
}

export interface PopupReferenceFieldProps {
    label: string;
    /** Entity id for view navigation and tooltip. */
    entityId: string | null;
    /** Shown in the read-only input. */
    displayName: string;
    /** Passed to ReferenceFieldViewIcon (e.g. workflow, set, schemaattribute). */
    viewEntityType: string;
    viewEntityLabel?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    description?: React.ReactNode;
    onSelect: () => void;
    onClear: () => void;
    /** When set and not disabled, allow typing/pasting an id (validated on blur, Enter, or after 1s idle). */
    idEntry?: ReferenceIdEntryConfig;
}

const committedFieldShellStyle: React.CSSProperties = {
    minHeight: 36,
    border: '1px solid var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-default)',
    backgroundColor: 'var(--mantine-color-body)',
};

/**
 * Reference field: display name + view / select (popup) / clear.
 * Optional id entry validates via getEntityById for allowed entity types.
 */
export const PopupReferenceField: React.FC<PopupReferenceFieldProps> = ({
    label,
    entityId,
    displayName,
    viewEntityType,
    viewEntityLabel,
    placeholder = 'None selected',
    required = false,
    disabled = false,
    error = false,
    description,
    onSelect,
    onClear,
    idEntry,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const idEntryEnabled = !!idEntry && !disabled;

    const idInput = useReferenceIdInput({
        enabled: idEntryEnabled,
        disabled,
        entityId: idEntry?.entityId ?? entityId,
        displayName: idEntry?.displayName ?? displayName,
        entityTypes: idEntry?.entityTypes ?? [],
        onCommit: idEntry?.onCommit ?? (() => {}),
        onClear: idEntry?.onClear ?? onClear,
    });

    const hasValue = Boolean((entityId ?? '').trim());
    const idTooltip = hasValue ? `ID: ${entityId}` : undefined;
    const iconCount = (hasValue ? 1 : 0) + 1 + (hasValue && !disabled ? 1 : 0);
    const rightSectionWidth = Math.max(40, iconCount * 36);

    const showCommittedNavDisplay =
        idEntryEnabled &&
        hasValue &&
        !idInput.isFocused &&
        !idInput.validating &&
        !idInput.validationError;

    const inputValue = idEntryEnabled ? idInput.draft : displayName;
    const resolvedPlaceholder = idEntryEnabled ? 'Enter ID or use search' : placeholder;
    const fieldLabel = <FieldLabelWithRequired label={label} required={required} readOnly={disabled} />;

    const actionIcons = (
        <Group gap={4} wrap="nowrap">
            {idInput.validating && <Loader size={16} />}
            {hasValue && !showCommittedNavDisplay && (
                <ReferenceFieldViewIcon
                    entityType={viewEntityType}
                    entityId={entityId!}
                    entityLabel={viewEntityLabel ?? viewEntityType}
                    disabled={disabled}
                />
            )}
            <Tooltip label={label ? `Select ${label}` : 'Select'}>
                <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={onSelect}
                    disabled={disabled}
                    aria-label={label ? `Select ${label}` : 'Select'}
                >
                    <IconSearch size={16} />
                </ActionIcon>
            </Tooltip>
            {hasValue && !disabled && (
                <Tooltip label="Clear">
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={onClear}
                        aria-label="Clear"
                    >
                        <IconX size={16} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );

    const beginIdEdit = () => {
        if (!idEntryEnabled || disabled) return;
        idInput.handleFocus();
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    return (
        <Box>
            {showCommittedNavDisplay ? (
                <Box>
                    <Text component="label" size="sm" fw={required ? 500 : 400} mb={4} display="block">
                        {fieldLabel}
                    </Text>
                    <Flex
                        align="center"
                        justify="space-between"
                        gap="sm"
                        px="sm"
                        py={6}
                        style={{
                            ...committedFieldShellStyle,
                            borderColor: error
                                ? 'var(--mantine-color-error)'
                                : 'var(--mantine-color-default-border)',
                        }}
                        onDoubleClick={beginIdEdit}
                        title="Double-click to edit ID"
                    >
                        <MultiReferenceEntityBadge
                            entityId={entityId!}
                            displayName={displayName || entityId!}
                            entityType={viewEntityType}
                            fallbackEntityTypes={idEntry?.entityTypes}
                            leftClickNavigation="sameTab"
                        />
                        {actionIcons}
                    </Flex>
                </Box>
            ) : (
                <Tooltip
                    label={idTooltip}
                    disabled={!idTooltip || idEntryEnabled}
                    withArrow
                    openDelay={300}
                >
                    <TextInput
                        ref={inputRef}
                        label={fieldLabel}
                        value={inputValue}
                        placeholder={resolvedPlaceholder}
                        readOnly={!idEntryEnabled}
                        disabled={disabled}
                        error={error || idInput.validationError || false}
                        onChange={idEntryEnabled ? (e) => idInput.handleChange(e.currentTarget.value) : undefined}
                        onBlur={idEntryEnabled ? idInput.handleBlur : undefined}
                        onFocus={idEntryEnabled ? idInput.handleFocus : undefined}
                        onKeyDown={idEntryEnabled ? idInput.handleKeyDown : undefined}
                        rightSection={
                            <Group gap={4} wrap="nowrap" pr="xs">
                                {actionIcons}
                            </Group>
                        }
                        rightSectionWidth={idInput.validating ? rightSectionWidth + 28 : rightSectionWidth}
                        rightSectionPointerEvents="auto"
                    />
                </Tooltip>
            )}
            {description && !(idEntryEnabled && idInput.validationError) ? (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {description}
                </Text>
            ) : null}
        </Box>
    );
};
